import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks/redux";
import { cloneDeep } from "lodash";
import { IWebsiteBuilderSelectSettings } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderSelect";
import React, { useState } from "react";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/websiteBuilder";
import { WebsiteBuilderPopupSections } from "../../../../helpers/popup/WebsiteBuilderPopupSections";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faPlus } from "@fortawesome/pro-regular-svg-icons";

interface ISelectSettingsProps {
  id: string;
  settings: IWebsiteBuilderSelectSettings;
}

export function SelectSettings(props: ISelectSettingsProps) {
  const { id, settings } = props;
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderSelectSettings>(cloneDeep(settings));

  function dispatchSettings(prevSettings: IWebsiteBuilderSelectSettings, nextSettings: IWebsiteBuilderSelectSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings({
        id,
        prevElementSettings: {
          type: "select",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "select",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderLabelSection() {
    return (
      <Input
        label={t("Label")}
        value={updatedSettings.label}
        onChange={e =>
          handleDebouncedUpdateState(prev => ({
            ...prev,
            label: e.target.value,
          }))
        }
      />
    );
  }

  function handleUpdateOption(value: string, index: number) {
    handleDebouncedUpdateState(prev => ({
      ...prev,
      options: [...prev.options.slice(0, index), value, ...prev.options.slice(index + 1)],
    }));
  }

  function handleRemoveOption(index: number) {
    handleUpdateState(prev => ({
      ...prev,
      options: [...prev.options.slice(0, index), ...prev.options.slice(index + 1)],
    }));
  }

  function handleAddOption() {
    handleUpdateState(prev => ({
      ...prev,
      options: [...prev.options, ""],
    }));
  }

  function renderOptionsSection() {
    return (
      <>
        <div className="website-builder-select-settings-options-title">List options</div>
        {/* TODO: Translation */}
        {updatedSettings.options.map((option, index) => (
          <div key={index} className="website-builder-select-settings-option">
            <Input
              labelHidden
              placeholder={`Option ${index + 1}`} // TODO: Translation
              value={option}
              onChange={e => handleUpdateOption(e.target.value, index)}
            />
            <FontAwesomeIcon className="option-close-icon" icon={faClose} onClick={() => handleRemoveOption(index)} />
          </div>
        ))}
        <Button
          className="website-builder-select-settings-add-option"
          type="text"
          size="small"
          onClick={handleAddOption}
        >
          <FontAwesomeIcon icon={faPlus} />
          {t("Add option")}
        </Button>
      </>
    );
  }

  function renderRequiredSection() {
    return (
      <Checkbox
        size="medium"
        label={t("Required")}
        value={updatedSettings.required}
        checked={updatedSettings.required}
        onChange={e =>
          handleUpdateState(prev => ({
            ...prev,
            required: e.target.checked,
          }))
        }
      />
    );
  }

  return (
    <WebsiteBuilderPopupSections sections={[renderLabelSection(), renderOptionsSection(), renderRequiredSection()]} />
  );
}
